import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LuArrowUp, LuBookOpen } from "react-icons/lu";
import { ChatApi } from "../../../api/chatApi";
import {
  useAuthStore,
  useIsUserSignedIn,
} from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useSubscriptionActions, useSubscriptionStore } from "../../../core_stores/useSubscriptionStore";
import { useAuthModal } from "../../../providers/AuthModalProvid";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { formatNumberWithSpaces } from "../../../utils/numberFormatter";
import {
  ALLOWED_IMAGE_TYPES,
  useAttachedImage,
  useAttachedDesign,
  useIsPromptSubmitting,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachedDesignChip } from "../../common_components/AttachedDesignChip";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";
import { PulsingBorder } from "./PulsingBorder";
import { components, typography, effects } from "../../../design/tokens";
import { useDesignLibraryModal } from "../../../providers/DesignLibraryModalProvider";
import { ModelSelectorBtn } from "../../project/components/ModelSelectorBtn";
import { useSelectedModel } from "../../../core_stores/useModelStore";

const MIN_TOKENS_FOR_NEW_PROJECT = 20000;

export function WelcomeTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const attachedDesign = useAttachedDesign();
  const isPromptSubmitting = useIsPromptSubmitting();
  const selectedModel = useSelectedModel();
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const placeholderContainerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const {
    setPrompt,
    setAttachedImage,
    setAttachedDesign,
    setIsSubmitting,
    isLengthExceeded: isPromptLengthExceeded,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { show: showAuthModal } = useAuthModal();
  const { open: openDesignLibrary } = useDesignLibraryModal();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();
  const { showSnackbar } = useSnackbar();

  // Focus on textarea when navigated with focusTextField flag
  useEffect(() => {
    if (location.state?.focusTextField && textareaRef.current) {
      textareaRef.current.focus();
      // Clear the state to prevent refocusing on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Focus textarea by default when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar(
        `Unsupported file type. Please select JPEG, PNG, or WebP images.`,
        { type: "error" }
      );
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveDesign = () => {
    setAttachedDesign(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  async function submit() {
    if (!prompt || prompt.trim() === "" || isPromptSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!isUserSignedIn) {
        await showAuthModal();
        const isUserSignedInAfterAuthModal =
          useAuthStore.getState().user !== null;
        if (!isUserSignedInAfterAuthModal) {
          return;
        }
      }

      // Always refresh token balance before checking, regardless of auth state
      await reloadSubscriptionsInfo();

      // Check if user has enough tokens before creating project
      const subscription = useSubscriptionStore.getState().subscription;
      // Round to integer to treat fractional tokens correctly
      if (!subscription || Math.round(subscription.remaining) < MIN_TOKENS_FOR_NEW_PROJECT) {
        showSnackbar(
          "Not enough tokens to start a new project. At least 20,000 tokens required.",
          { type: "error" }
        );
        openSubscriptionsModal();
        return;
      }

      // Combine user prompt with design prompt if a design is attached
      const finalPrompt = attachedDesign
        ? `${prompt}\n\n[[DESIGN_START]]\n${attachedDesign.promptText}\n[[DESIGN_END]]`
        : prompt;

      const projectId = await ChatApi.createNewProject();
      clearPromptInput();
      navigate(`/project/${projectId}`, {
        state: {
          projectId,
          initialPrompt: finalPrompt,
          initialImage: attachedImage,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`${isCompactView && "px-4"} w-full z-20`}>
      <PulsingBorder isFocused={isFocused}>
        <div
          className="h-full bg-dark-surface-secondary
          rounded-xl p-3 space-y-2
          border border-dark-border
          flex flex-col
          shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          {/* Attached image preview - above text input */}
          {attachedImage && (
            <div className="px-2 pt-1">
              <AttachedImageIcon
                attachedImg={attachedImage}
                onRemove={handleRemoveImage}
              />
            </div>
          )}
          <div className="relative w-full flex-1">
            <textarea
              className="w-full h-[125px]
              bg-transparent rounded-lg
              border-0
              resize-none focus:outline-none
              text-dark-text
              py-3 px-4
              placeholder:text-dark-placeholder"
              value={prompt ?? ""}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              ref={textareaRef}
            />
          {!prompt && (
            <div ref={placeholderContainerRef} className="absolute top-3 left-4 right-4 z-0">
              <CustomPlaceholder containerRef={placeholderContainerRef} />
            </div>
          )}
        </div>
        {isPromptLengthExceeded() && (
          <div className="text-red-500 text-sm px-2">
            Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
            characters allowed.
          </div>
        )}
        <div className="flex justify-between items-center gap-4 px-2 pt-2 border-t border-dark-border">
          <div className={`flex items-center min-w-0 ${isCompactView ? 'gap-2' : 'gap-4'}`}>
            <AttachmentsBtn
              className="flex-shrink-0 w-9 h-9 bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full transition-colors duration-200"
              onAddImageClick={handleUploadClick}
              iconSize={20}
            />
            <ModelSelectorBtn
              className="flex-shrink-0 h-9 bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full px-3 transition-colors duration-200"
              isDisabled={isPromptSubmitting}
            />
            {!attachedDesign && (
              <div className="relative group">
                <button
                  onClick={() => {
                    if (selectedModel === "claude-opus-4-5" || selectedModel === "auto") {
                      openDesignLibrary();
                    }
                  }}
                  disabled={selectedModel !== "claude-opus-4-5" && selectedModel !== "auto"}
                  className={`h-9 flex items-center gap-2 px-3 rounded-full
                    ${effects.transition.default}
                    ${selectedModel === "claude-opus-4-5" || selectedModel === "auto"
                      ? "bg-dark-surface-tertiary hover:bg-dark-hover text-dark-text-muted hover:text-dark-text cursor-pointer"
                      : "bg-dark-surface-tertiary/50 text-dark-text-muted/50 cursor-not-allowed"
                    }`}
                  aria-label="Open Design Library"
                >
                  <LuBookOpen size={20} />
                  {!isCompactView && (
                    <span className={`${typography.size.sm} ${typography.weight.medium}`}>Design</span>
                  )}
                </button>

                {/* Tooltip - only show when NOT Opus 4.5 and NOT Auto */}
                {selectedModel !== "claude-opus-4-5" && selectedModel !== "auto" && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                    opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200
                    bg-black border border-gray-700 rounded-lg px-3 py-2 shadow-lg z-50 whitespace-nowrap">
                    <p className="text-sm text-white font-medium">Design Library requires Auto or Opus 4.5</p>
                    <p className="text-xs text-gray-400 mt-1">Select Auto or Opus 4.5 for best UI design results</p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1
                      border-4 border-transparent border-t-gray-700" />
                  </div>
                )}
              </div>
            )}
            {attachedDesign && (
              <AttachedDesignChip
                designName={attachedDesign.name}
                description={attachedDesign.description}
                onRemove={handleRemoveDesign}
                iconOnly={isCompactView}
              />
            )}
          </div>

          <SubmitBtn onClick={submit} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />
        </div>
      </PulsingBorder>
    </div>
  );
}

function CustomPlaceholder({
  containerRef
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) {
  const [animatedText, setAnimatedText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [dotCount, setDotCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const { setPrompt } = usePromptInputActions();

  // Measure text width utility
  const measureTextWidth = (text: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;

    // Match the actual font used in the component
    context.font = '400 16px Roboto, system-ui, -apple-system, sans-serif';
    return context.measureText(text).width;
  };

  // Track container width with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth(); // Initial measurement

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  // Determine which text to use based on measurement
  const longText = "Ask Darvin to create a mobile app";
  const shortText = "Create a mobile app";

  const longTextWidth = measureTextWidth(longText + "..."); // Measure with 3 dots (worst case)
  const availableWidth = containerWidth - 64; // Account for padding (left-4 + right-4 from absolute positioning)
  const bufferWidth = availableWidth * 0.9; // 10% buffer

  const baseText = longTextWidth <= bufferWidth ? longText : shortText;
  const fullText = baseText + "...";

  // Typewriter effect
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setAnimatedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        setIsTypingComplete(true);
      }
    }, 50); // 50ms per character

    return () => clearInterval(interval);
  }, [fullText]);

  // Animated dots after typing completes
  useEffect(() => {
    if (!isTypingComplete) return;

    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 0 : prev + 1));
    }, 400); // 400ms per dot cycle

    return () => clearInterval(interval);
  }, [isTypingComplete]);

  const handlePlaceholderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPrompt("");
    // Focus the textarea with a small delay to ensure the text is set first
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        // Move cursor to end
        textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
      }
    }, 0);
  };

  const displayText = isTypingComplete
    ? baseText + ".".repeat(dotCount)
    : animatedText;

  return (
    <div
      className="text-dark-placeholder cursor-text"
      onClick={handlePlaceholderClick}
    >
      {displayText}
    </div>
  );
}

function SubmitBtn({ onClick }: { onClick: () => void }) {
  const { isLengthExceeded: isPromptLengthExceeded } = usePromptInputActions();
  const isPromptSubmitting = useIsPromptSubmitting();
  const isCompactView = useIsCompactView();
  const prompt = usePrompt();
  const isEmpty = !prompt || prompt.trim() === "";
  const isDisabled = isPromptSubmitting || isPromptLengthExceeded() || isEmpty;

  return (
    <div className="h-9 flex gap-2">
      <button
        id="home-generate"
        className={isCompactView
          ? `w-9 h-9 bg-primary-blue text-white rounded-full
             hover:opacity-90 transition-opacity duration-300
             disabled:opacity-50 disabled:cursor-not-allowed
             flex items-center justify-center`
          : `${components.button.primary} disabled:cursor-not-allowed whitespace-nowrap shrink-0`
        }
        onClick={onClick}
        disabled={isDisabled}
      >
        {isPromptSubmitting ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
        ) : isCompactView ? (
          <LuArrowUp className="w-5 h-5" />
        ) : (
          <span className="whitespace-nowrap">Build my app</span>
        )}
      </button>
    </div>
  );
}
